import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useEvent, useUnit } from 'effector-react';
import { MenuTree } from '@nx-frontend/features/menu-tree';
import { useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { useCustomizerCheckChanges } from '@nx-frontend/shared/hooks/use-customizer-check-changes';
import { $app } from '@nx-frontend/shared/store/app';
import { bringAttentionToCentralPartEvent } from '@nx-frontend/shared/store/client-layout';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { getIsMenuItemExpandedFx, toggleExpandItemFx } from '@nx-frontend/shared/store/feature-menu-tree';
import {
  fetchAllParentFoldersIntemsFx,
  fetchItemsInOpenendFoldersFx,
} from '@nx-frontend/shared/store/internal-space-menu';
import { setIsSidebarScrollRestrictedEvent } from '@nx-frontend/shared/store/sidebar';
import { $sidebarDnd } from '@nx-frontend/shared/store/sidebar-dnd';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $workspaceAccess } from '@nx-frontend/shared/store/workspace-access';
import { MenuItem, MenuItemType, MenuType, WorkspaceRole } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { useSidebarDragOver } from '../hooks';
import { useOpenKitchenMenuItem } from '../hooks/kitchen';
import { useOpenKitchenMenuContextNote } from '../hooks/kitchen';
import { useOpenKitchenMenuContextFolder } from '../hooks/kitchen';
import { useOpenKitchenMenuContextFolders } from '../hooks/kitchen';
import { useOpenKitchenCreateContext } from '../hooks/kitchen/open-create-context';
import { useOpenKitchenMenuContextTag } from '../hooks/kitchen/open-menu-context-tag';
import { useOpenKitchenMenuEmojiDialog } from '../hooks/kitchen/open-menu-emoji-dialog';
import { createAbortController, stopAbortController } from '../page-abort-controller';

const MAX_CHILDREN_ITEMS_TO_RENDER = 200;

export function SidebarKitchenMenu() {
  const [hideAddButtonItemTypes, setHideAddButtonItemTypes] = useState<MenuItemType[]>([]);
  const [unleashFlags, workspaceAccess, app, currentWorkspace, sidebarDnd] = useUnit([
    $unleashFlags,
    $workspaceAccess,
    $app,
    $currentWorkspace,
    $sidebarDnd,
  ]);
  const [
    getIsMenuItemExpanded,
    fetchItemsInOpenendFolders,
    bringAttentionToCentralPart,
    setIsSidebarScrollRestricted,
    toggleExpandItem,
  ] = useEvent([
    getIsMenuItemExpandedFx,
    fetchItemsInOpenendFoldersFx,
    bringAttentionToCentralPartEvent,
    setIsSidebarScrollRestrictedEvent,
    toggleExpandItemFx,
  ]);
  const openKitchenMenuItem = useOpenKitchenMenuItem();
  const openKitchenMenuContextNote = useOpenKitchenMenuContextNote();
  const openKitchenMenuContextFolder = useOpenKitchenMenuContextFolder();
  const openKitchenMenuContextFolders = useOpenKitchenMenuContextFolders();
  const openKitchenMenuContextTag = useOpenKitchenMenuContextTag();
  const openKitchenCreateContext = useOpenKitchenCreateContext();
  const openKitchenMenuEmojiDialog = useOpenKitchenMenuEmojiDialog();
  const customizerCheckChanges = useCustomizerCheckChanges();
  const dragOver = useSidebarDragOver();

  const params = useParams();
  const slug = useMemo(() => params?.slug || [], [params?.slug]) as string[];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pathWorkspaceId, pageType, itemId] = slug;

  useEffect(() => {
    const result = [
      MenuItemType.NOTE,
      MenuItemType.ALL_PAGES,
      MenuItemType.FILES_MANAGER,
      MenuItemType.TASKS,
      MenuItemType.CHAT,
      MenuItemType.TAG,
      MenuItemType.AI_AGENTS,
    ];
    // FIXME: better rewrite to check an RBAC privilege
    if (![WorkspaceRole.ADMIN, WorkspaceRole.EDITOR].includes(workspaceAccess.role)) {
      result.push(...[MenuItemType.FOLDERS, MenuItemType.NOTES_FOLDER, MenuItemType.TAGS]);
    }
    setHideAddButtonItemTypes(result);
  }, [workspaceAccess]);

  const handleMenuItemToggle = useCallback(
    async (menuItem: MenuItem) => {
      const isExpanded = await getIsMenuItemExpanded({ id: menuItem.id });

      if (!isExpanded) {
        stopAbortController();
        const pagesAbortController = createAbortController();
        fetchItemsInOpenendFolders({
          sessionId: app.sessionId,
          workspaceId: currentWorkspace.globalId,
          parentKey: menuItem.id,
          abortController: pagesAbortController,
        });
      }
    },
    [app.sessionId, currentWorkspace.globalId, fetchItemsInOpenendFolders, getIsMenuItemExpanded]
  );

  const handleMenuItemClick = useCallback(
    (menuItem: MenuItem) => {
      if (menuItem.type === MenuItemType.TAGS) {
        toggleExpandItem({
          item: menuItem,
          menuType: MenuType.WEBNOTES_MENU,
        });
        return false;
      }
      return customizerCheckChanges(() => {
        openKitchenMenuItem(menuItem);
      });
    },
    [customizerCheckChanges, openKitchenMenuItem, toggleExpandItem]
  );

  const handleMenuItemMoreClick = useCallback(
    (menuItem: MenuItem | undefined, e: MouseEvent, useMousePos?: boolean) => {
      if (menuItem) {
        if (menuItem.type === MenuItemType.FOLDERS) {
          openKitchenMenuContextFolders(e, useMousePos);
        } else if (menuItem.type === MenuItemType.NOTES_FOLDER) {
          openKitchenMenuContextFolder(menuItem, e, useMousePos);
        } else if (menuItem.type === MenuItemType.NOTE) {
          openKitchenMenuContextNote(menuItem, e, useMousePos);
        } else if (menuItem.type === MenuItemType.TAG) {
          openKitchenMenuContextTag(menuItem, e, useMousePos);
        }
      }
    },
    [openKitchenMenuContextFolder, openKitchenMenuContextFolders, openKitchenMenuContextNote, openKitchenMenuContextTag]
  );

  const handleMenuItemAddClick = useCallback(
    (menuItem: MenuItem | undefined, e: MouseEvent) => {
      if (menuItem) {
        openKitchenCreateContext(menuItem, e);
      }
    },
    [openKitchenCreateContext]
  );

  const handleMenuItemIconClick = useCallback(
    (menuItem: MenuItem | undefined, e: MouseEvent) => {
      if (menuItem) {
        openKitchenMenuEmojiDialog(menuItem, e);
      }
    },
    [openKitchenMenuEmojiDialog]
  );

  const onMoreItemsClickCallback = useCallback(
    (item: MenuItem) => {
      if (item.id === 'notesFolder#' + itemId) {
        bringAttentionToCentralPart();
      } else {
        setIsSidebarScrollRestricted(true);
        const unwatch = fetchAllParentFoldersIntemsFx.doneData.watch((params) => {
          setIsSidebarScrollRestricted(false);
          bringAttentionToCentralPart();
          unwatch();
        });
        handleMenuItemClick(item);
      }
    },
    [bringAttentionToCentralPart, handleMenuItemClick, itemId, setIsSidebarScrollRestricted]
  );

  return (
    <div>
      <MenuTree
        activeDragId={sidebarDnd.activeItemId}
        menuType={MenuType.WEBNOTES_MENU}
        multiSelectEnabled={false}
        excludeIds={[
          ...(!unleashFlags[UnleashFeatures.FILE_MANAGER]
            ? [`${MenuItemType.FILES_MANAGER}#${MenuItemType.FILES_MANAGER}`]
            : []),
          ...(!unleashFlags[UnleashFeatures.NOTIFICATION_PANEL] ? [] : [`${MenuItemType.CHAT}#${MenuItemType.CHAT}`]),
        ]}
        hideMenuButtonItemTypes={[
          MenuItemType.ALL_PAGES,
          MenuItemType.FILES_MANAGER,
          MenuItemType.TASKS,
          MenuItemType.CHAT,
          MenuItemType.TAGS,
          MenuItemType.AI_AGENTS,
          ...(workspaceAccess.role === WorkspaceRole.READER ? [MenuItemType.TAG] : []),
        ]}
        disabledDragItemTypes={[
          MenuItemType.ALL_PAGES,
          MenuItemType.FILES_MANAGER,
          MenuItemType.TASKS,
          MenuItemType.CHAT,
          MenuItemType.FOLDERS,
          MenuItemType.TAGS,
          MenuItemType.TAG,
          MenuItemType.TAGS,
          MenuItemType.TAG,
          MenuItemType.AI_AGENTS,
        ]}
        disabledDropItemTypes={[
          MenuItemType.ALL_PAGES,
          MenuItemType.FILES_MANAGER,
          MenuItemType.TASKS,
          MenuItemType.CHAT,
          MenuItemType.FOLDERS,
          MenuItemType.TAGS,
          MenuItemType.TAG,
          MenuItemType.AI_AGENTS,
        ]}
        onMenuItemClick={handleMenuItemClick}
        hideAddButtonItemTypes={hideAddButtonItemTypes}
        allowClickIconItemTypes={[MenuItemType.NOTE, MenuItemType.NOTES_FOLDER]}
        onMenuItemMore={handleMenuItemMoreClick}
        onMenuItemAdd={handleMenuItemAddClick}
        onMenuItemIcon={handleMenuItemIconClick}
        onMenuItemToggle={handleMenuItemToggle}
        onDragOver={(id) => dragOver(id, MenuType.WEBNOTES_MENU)}
        onMenuItemRightClick={(menuItem, event) => handleMenuItemMoreClick(menuItem, event, true)}
        maxChildrenItemsToRender={
          unleashFlags[UnleashFeatures.SIDEBAR_TRUNCATE_LONG_CHILDREN] ? MAX_CHILDREN_ITEMS_TO_RENDER : 0
        }
        onMoreItemsClick={onMoreItemsClickCallback}
      />
    </div>
  );
}
